import { Component, inject, OnInit } from '@angular/core';
import { CommonService } from '../common.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { NgSelectComponent } from '@ng-select/ng-select';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

interface Item { id: string; name: string;[k: string]: any; }

@Component({
  selector: 'app-products-filter',
  imports: [FormsModule, ReactiveFormsModule, NgSelectComponent],
  templateUrl: './products-filter.component.html',
  styleUrl: './products-filter.component.scss'
})

export class ProductsFilterComponent implements OnInit {
  filterForm!: FormGroup;
  today: Date = new Date();

  metaData: any;

  // raw lists from metaData (no ALL)
  allVendors: Item[] = [];
  allManufacturers: Item[] = [];
  allBrands: Item[] = [];
  allDepartments: Item[] = [];
  allCategories: Item[] = [];
  allSubCategories: Item[] = [];

  // visible (after filtering by relations + search)
  vendorsVisible: Item[] = [];
  manufacturersVisible: Item[] = [];
  brandsVisible: Item[] = [];
  departmentsVisible: Item[] = [];
  categoriesVisible: Item[] = [];
  subCategoriesVisible: Item[] = [];

  // relations
  vendorManufacturersRel: any[] = [];
  vendorCategoriesRel: any[] = [];

  // selections (store ids)
  selectedVendors: string[] = [];
  selectedManufacturers: string[] = [];
  selectedBrands: string[] = [];
  selectedDepartments: string[] = [];
  selectedCategories: string[] = [];
  selectedSubCategories: string[] = [];

  // select-all flags
  allVendorsSelected = false;
  allManufacturersSelected = false;
  allBrandsSelected = false;
  allDepartmentsSelected = false;
  allCategoriesSelected = false;
  allSubCategoriesSelected = false;
  uniqueDaysOfWeek: string[] = [];
  uniqueDaysOfMonth: number[] = [];
  private scheduleWeekMap: Map<string, Set<string>> = new Map(); // dayOfWeek -> set of vendorIds
  private scheduleMonthMap: Map<number, Set<string>> = new Map(); // dayOfMonth -> set of vendorIds


  private subs = new Subscription();

  constructor(private fb: FormBuilder, private commonService: CommonService, public activeModal: NgbActiveModal) { }

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      poNumber: ['PO-' + Date.now()],
      vendorSearch: [''],
      manufacturerSearch: [''],
      brandSearch: [''],
      departmentSearch: [''],
      categorySearch: [''],
      subCategorySearch: [''],
      vendorDaysOfWeek: [[]],      // array of selected weekday strings
      vendorDaysOfMonth: [[]]
    });

    this.subs.add(
      this.commonService.getFilterList().subscribe({
        next: (data: any) => {
          this.metaData = data;
          this.initFromMeta();
        },
        error: (err) => {
          console.error('Error fetching filter list', err);
        }
      })
    );

    // optional: react to search control valueChanges (debounce can be added if needed)
    this.subs.add(
      this.filterForm.get('vendorSearch')!.valueChanges.subscribe(() => this.applySearchAll())
    );
    this.subs.add(
      this.filterForm.get('manufacturerSearch')!.valueChanges.subscribe(() => this.applySearchAll())
    );
    this.subs.add(
      this.filterForm.get('brandSearch')!.valueChanges.subscribe(() => this.applySearchAll())
    );
    this.subs.add(
      this.filterForm.get('departmentSearch')!.valueChanges.subscribe(() => this.applySearchAll())
    );
    this.subs.add(
      this.filterForm.get('categorySearch')!.valueChanges.subscribe(() => this.applySearchAll())
    );
    this.subs.add(
      this.filterForm.get('subCategorySearch')!.valueChanges.subscribe(() => this.applySearchAll())
    );
    this.subs.add(this.filterForm.get('vendorDaysOfWeek')!.valueChanges.subscribe((v: string[]) => this.onDaysOfWeekChange(v)));
    this.subs.add(this.filterForm.get('vendorDaysOfMonth')!.valueChanges.subscribe((v: number[]) => this.onDaysOfMonthChange(v)));
  }

  private onDaysOfWeekChange(selectedDays: string[] = []): void {
    // collect vendorIds from selected weekdays
    const vendorSet = new Set<string>();
    (selectedDays || []).forEach(day => {
      const set = this.scheduleWeekMap.get(day);
      if (set) set.forEach(v => vendorSet.add(v));
    });

    // merge with any vendors from selected day-of-month too (so both selections accumulate)
    const selectedMonthDays: number[] = this.filterForm.get('vendorDaysOfMonth')!.value || [];
    (selectedMonthDays || []).forEach(d => {
      const set = this.scheduleMonthMap.get(Number(d));
      if (set) set.forEach(v => vendorSet.add(v));
    });

    // set selectedVendors and sync UI
    this.selectedVendors = Array.from(vendorSet);
    // update select-all flag relative to currently visible vendors
    this.allVendorsSelected = this.vendorsVisible.length > 0 && this.selectedVendors.length === this.vendorsVisible.length;
    // clear dependent selections because vendor selection affects them
    this.selectedManufacturers = [];
    this.selectedBrands = [];
    this.selectedCategories = [];
    this.selectedSubCategories = [];
    this.applySearchAll();
  }


  // 5) When user selects day-of-month -> auto-select vendors
  private onDaysOfMonthChange(selectedDays: number[] = []): void {
    // collect vendorIds from selected month days
    const vendorSet = new Set<string>();
    (selectedDays || []).forEach(d => {
      const set = this.scheduleMonthMap.get(Number(d));
      if (set) set.forEach(v => vendorSet.add(v));
    });

    // merge with any vendors from selected weekdays too
    const selectedWeekDays: string[] = this.filterForm.get('vendorDaysOfWeek')!.value || [];
    (selectedWeekDays || []).forEach(day => {
      const set = this.scheduleWeekMap.get(day);
      if (set) set.forEach(v => vendorSet.add(v));
    });

    // set selectedVendors and sync UI
    this.selectedVendors = Array.from(vendorSet);
    this.allVendorsSelected = this.vendorsVisible.length > 0 && this.selectedVendors.length === this.vendorsVisible.length;
    // clear dependent selections because vendor selection affects them
    this.selectedManufacturers = [];
    this.selectedBrands = [];
    this.selectedCategories = [];
    this.selectedSubCategories = [];
    this.applySearchAll();
  }

  private initFromMeta(): void {
    const e = this.metaData?.entities || {};
    const r = this.metaData?.relations || {};

    // store raw lists
    this.allVendors = e.vendors || [];
    this.allManufacturers = e.manufacturers || [];
    this.allBrands = e.brands || [];
    this.allDepartments = e.departments || [];
    this.allCategories = e.categories || [];
    this.allSubCategories = e.subCategories || [];

    this.vendorManufacturersRel = r.vendorManufacturers || [];
    this.vendorCategoriesRel = r.vendorCategories || [];
    this.processSchedules(this.metaData?.schedules || { weekly: [], monthly: [] });
    // initialize visible lists (no selection yet)
    this.resetAllVisibles();
    this.resetSelections();
  }

  private processSchedules(schedules: { weekly?: any[]; monthly?: any[] }): void {
    this.scheduleWeekMap.clear();
    this.scheduleMonthMap.clear();

    // weekly: [{ vendorId, dayOfWeek }]
    (schedules.weekly || []).forEach((w: any) => {
      const day = (w.dayOfWeek || '').toString();
      if (!day) return;
      if (!this.scheduleWeekMap.has(day)) this.scheduleWeekMap.set(day, new Set());
      this.scheduleWeekMap.get(day)!.add(w.vendorId);
    });

    // monthly: [{ vendorId, dayOfMonth }]
    (schedules.monthly || []).forEach((m: any) => {
      const dayNum = Number(m.dayOfMonth);
      if (!dayNum) return;
      if (!this.scheduleMonthMap.has(dayNum)) this.scheduleMonthMap.set(dayNum, new Set());
      this.scheduleMonthMap.get(dayNum)!.add(m.vendorId);
    });

    // unique lists for UI (sorted)
    const dayOrder = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

    this.uniqueDaysOfWeek = Array.from(this.scheduleWeekMap.keys())
      .sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

    this.uniqueDaysOfMonth = Array.from(this.scheduleMonthMap.keys())
      .sort((a, b) => Number(a) - Number(b));

  }

  // reset visible lists to raw lists and apply search
  resetAllVisibles(): void {
    this.vendorsVisible = [...this.allVendors];
    this.manufacturersVisible = [...this.allManufacturers];
    this.brandsVisible = [...this.allBrands];
    this.departmentsVisible = [...this.allDepartments];
    this.categoriesVisible = [...this.allCategories];
    this.subCategoriesVisible = [...this.allSubCategories];
    this.applySearchAll();
  }

  // reset selections and select-all flags and clear search inputs
  resetSelections(): void {
    this.selectedVendors = [];
    this.selectedManufacturers = [];
    this.selectedBrands = [];
    this.selectedDepartments = [];
    this.selectedCategories = [];
    this.selectedSubCategories = [];

    this.allVendorsSelected = false;
    this.allManufacturersSelected = false;
    this.allBrandsSelected = false;
    this.allDepartmentsSelected = false;
    this.allCategoriesSelected = false;
    this.allSubCategoriesSelected = false;

    // clear search fields
    this.filterForm.patchValue({
      vendorSearch: '',
      manufacturerSearch: '',
      brandSearch: '',
      departmentSearch: '',
      categorySearch: '',
      subCategorySearch: ''
    }, { emitEvent: false });

    // recompute visibles
    this.resetAllVisibles();
  }

  // apply all searches + relations to compute visible lists
  applySearchAll(): void {
    const qV = (this.filterForm.get('vendorSearch')!.value || '').trim().toLowerCase();
    const qM = (this.filterForm.get('manufacturerSearch')!.value || '').trim().toLowerCase();
    const qB = (this.filterForm.get('brandSearch')!.value || '').trim().toLowerCase();
    const qD = (this.filterForm.get('departmentSearch')!.value || '').trim().toLowerCase();
    const qC = (this.filterForm.get('categorySearch')!.value || '').trim().toLowerCase();
    const qS = (this.filterForm.get('subCategorySearch')!.value || '').trim().toLowerCase();

    // vendors
    this.vendorsVisible = !qV ? this.allVendors.slice() : this.allVendors.filter(i => (i.name || '').toLowerCase().includes(qV));

    // departments
    this.departmentsVisible = !qD ? this.allDepartments.slice() : this.allDepartments.filter(i => (i.name || '').toLowerCase().includes(qD));

    // manufacturers base (may be filtered by selected vendors)
    let manufacturersBase = this.allManufacturers.slice();
    if (this.selectedVendors?.length) {
      const rels = this.vendorManufacturersRel
        .filter((v: any) => this.selectedVendors.includes(v.vendorId))
        .map((v: any) => v.manufacturerId);
      const unique = Array.from(new Set(rels));
      manufacturersBase = this.allManufacturers.filter(m => unique.includes(m.id));
    }
    this.manufacturersVisible = !qM ? manufacturersBase.slice() : manufacturersBase.filter(i => (i.name || '').toLowerCase().includes(qM));

    // categories base (may be filtered by department and/or vendor)
    let categoriesBase = this.allCategories.slice();
    if (this.selectedDepartments?.length) {
      categoriesBase = categoriesBase.filter((c: any) => this.selectedDepartments.includes(c.departmentId));
    }
    if (this.selectedVendors?.length && this.vendorCategoriesRel?.length) {
      const vendorCatIds = this.vendorCategoriesRel
        .filter((v: any) => this.selectedVendors.includes(v.vendorId))
        .map((v: any) => v.categoryId);
      const unique = Array.from(new Set(vendorCatIds));
      if (unique.length) categoriesBase = categoriesBase.filter(c => unique.includes(c.id));
    }
    this.categoriesVisible = !qC ? categoriesBase.slice() : categoriesBase.filter(i => (i.name || '').toLowerCase().includes(qC));

    // brands depend on selected manufacturers
    // brands (show all by default)
    let brandsBase = this.allBrands.slice();
    if (this.selectedManufacturers?.length) {
      brandsBase = brandsBase.filter((b: any) => this.selectedManufacturers.includes(b.manufacturerId));
    }
    this.brandsVisible = !qB ? brandsBase.slice() : brandsBase.filter(i => (i.name || '').toLowerCase().includes(qB));

    // subCategories depend on selected categories
    // subCategories (show all by default)
    let subsBase = this.allSubCategories.slice();
    if (this.selectedCategories?.length) {
      subsBase = subsBase.filter((sc: any) => this.selectedCategories.includes(sc.categoryId));
    }
    this.subCategoriesVisible = !qS ? subsBase.slice() : subsBase.filter(i => (i.name || '').toLowerCase().includes(qS));


    this.syncSelectAllFlags();
  }

  // SELECT ALL HANDLER
  toggleSelectAll(column: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    switch (column) {
      case 'vendors':
        this.allVendorsSelected = checked;
        this.selectedVendors = checked ? this.vendorsVisible.map(v => v.id) : [];
        this.selectedManufacturers = [];
        this.selectedBrands = [];
        this.selectedCategories = [];
        this.selectedSubCategories = [];
        break;
      case 'manufacturers':
        this.allManufacturersSelected = checked;
        this.selectedManufacturers = checked ? this.manufacturersVisible.map(m => m.id) : [];
        this.selectedBrands = [];
        this.selectedCategories = [];
        this.selectedSubCategories = [];
        break;
      case 'brands':
        this.allBrandsSelected = checked;
        this.selectedBrands = checked ? this.brandsVisible.map(b => b.id) : [];
        break;
      case 'departments':
        this.allDepartmentsSelected = checked;
        this.selectedDepartments = checked ? this.departmentsVisible.map(d => d.id) : [];
        this.selectedCategories = [];
        this.selectedSubCategories = [];
        break;
      case 'categories':
        this.allCategoriesSelected = checked;
        this.selectedCategories = checked ? this.categoriesVisible.map(c => c.id) : [];
        this.selectedSubCategories = [];
        break;
      case 'subCategories':
        this.allSubCategoriesSelected = checked;
        this.selectedSubCategories = checked ? this.subCategoriesVisible.map(s => s.id) : [];
        break;
    }
    this.applySearchAll();
  }

  // INDIVIDUAL TOGGLE
  onToggleItem(column: string, id: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    const arrFor = (col: string) => {
      switch (col) {
        case 'vendors': return this.selectedVendors;
        case 'manufacturers': return this.selectedManufacturers;
        case 'brands': return this.selectedBrands;
        case 'departments': return this.selectedDepartments;
        case 'categories': return this.selectedCategories;
        case 'subCategories': return this.selectedSubCategories;
        default: return [];
      }
    };

    const arr = arrFor(column);
    if (checked) {
      if (!arr.includes(id)) arr.push(id);
    } else {
      const idx = arr.indexOf(id);
      if (idx > -1) arr.splice(idx, 1);
    }

    switch (column) {
      case 'vendors': this.vendorToggled(); break;
      case 'manufacturers': this.manufacturerToggled(); break;
      case 'brands': this.brandToggled(); break;
      case 'departments': this.departmentToggled(); break;
      case 'categories': this.categoryToggled(); break;
      case 'subCategories': this.subCategoryToggled(); break;
    }
  }

  vendorToggled(): void {
    this.allVendorsSelected = this.vendorsVisible.length > 0 && this.selectedVendors.length === this.vendorsVisible.length;
    this.selectedManufacturers = [];
    this.selectedBrands = [];
    this.selectedCategories = [];
    this.selectedSubCategories = [];
    this.applySearchAll();
  }

  manufacturerToggled(): void {
    this.allManufacturersSelected = this.manufacturersVisible.length > 0 && this.selectedManufacturers.length === this.manufacturersVisible.length;
    this.selectedBrands = [];
    this.selectedCategories = [];
    this.selectedSubCategories = [];
    this.applySearchAll();
  }

  brandToggled(): void {
    this.allBrandsSelected = this.brandsVisible.length > 0 && this.selectedBrands.length === this.brandsVisible.length;
  }

  departmentToggled(): void {
    this.allDepartmentsSelected = this.departmentsVisible.length > 0 && this.selectedDepartments.length === this.departmentsVisible.length;
    this.selectedCategories = [];
    this.selectedSubCategories = [];
    this.applySearchAll();
  }

  categoryToggled(): void {
    this.allCategoriesSelected = this.categoriesVisible.length > 0 && this.selectedCategories.length === this.categoriesVisible.length;
    this.selectedSubCategories = [];
    this.applySearchAll();
  }

  subCategoryToggled(): void {
    this.allSubCategoriesSelected = this.subCategoriesVisible.length > 0 && this.selectedSubCategories.length === this.subCategoriesVisible.length;
  }

  private syncSelectAllFlags(): void {
    this.allVendorsSelected = this.vendorsVisible.length > 0 && this.selectedVendors.length === this.vendorsVisible.length;
    this.allManufacturersSelected = this.manufacturersVisible.length > 0 && this.selectedManufacturers.length === this.manufacturersVisible.length;
    this.allBrandsSelected = this.brandsVisible.length > 0 && this.selectedBrands.length === this.brandsVisible.length;
    this.allDepartmentsSelected = this.departmentsVisible.length > 0 && this.selectedDepartments.length === this.departmentsVisible.length;
    this.allCategoriesSelected = this.categoriesVisible.length > 0 && this.selectedCategories.length === this.categoriesVisible.length;
    this.allSubCategoriesSelected = this.subCategoriesVisible.length > 0 && this.selectedSubCategories.length === this.subCategoriesVisible.length;
  }

  // BUILD PAYLOAD & SUBMIT
  buildPayload(): any {
    return {
      vendorIds: this.selectedVendors || [],
      manufacturerIds: this.selectedManufacturers || [],
      brandIds: this.selectedBrands || [],
      departmentIds: this.selectedDepartments || [],
      categoryIds: this.selectedCategories || [],
      subCategoryIds: this.selectedSubCategories || []
    };
  }

  onSubmit(): void {
    const payload = this.buildPayload();
    this.activeModal.close(payload);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }



}
